import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CtaButton } from "@/components/CtaButton";
import { Eyebrow } from "@/components/Reveal";
import { LeopardStrip } from "@/components/Leopard";
import { site } from "@/config/site.config";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} data-testid="hero-section" className="spotlight relative overflow-hidden">
      <LeopardStrip className="h-3 sm:h-4" />
      <div className="mx-auto max-w-4xl px-5 pb-14 pt-12 text-center sm:pt-16 lg:pt-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <Eyebrow>FOR THE WOMAN WHO KNOWS THERE’S MORE</Eyebrow>
          <p className="mt-5 font-serif text-2xl italic text-cream sm:text-3xl" data-testid="brand-lockup">
            WILD WOMAN <span className="text-lime">MILLIONS</span>™
          </p>
        </motion.div>

        <h1 className="mt-6 text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskLine delay={0.1}>YOU DON’T NEED A SMALLER VISION.</MaskLine>
          <MaskLine delay={0.25}>
            YOU NEED THE <em className="font-serif font-medium italic text-lime">CAPACITY</em>
          </MaskLine>
          <MaskLine delay={0.4}>TO HOLD MORE.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
        >
          <div className="mx-auto mt-5 max-w-xl space-y-3 text-[15px] text-cream/70 sm:mt-6 sm:text-lg">
            <p>Money. Love. Pleasure. Freedom. Motherhood. Creativity. Impact.</p>
            <p className="text-cream">Not someday. In the life you’re actually living.</p>
          </div>

          <p className="mt-6 font-mono text-[11px] tracking-[0.3em] text-lime sm:mt-8">
            VISION → CAPACITY → CURRENCY™
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4 sm:gap-5">
            <span data-testid="hero-price" className="font-serif text-5xl font-bold text-cream sm:text-7xl">
              {site.price}
            </span>
            <CtaButton id="hero">ENTER WILD WOMAN MILLIONS</CtaButton>
          </div>
          <p className="mt-3 font-mono text-[11px] tracking-[0.2em] text-cream/40">
            YOUR VISION BLUEPRINT + 7 QUANTUM HABITS™ + THE CAPACITY TO HOLD MORE
          </p>
          <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-cream/30">
            ONE PAYMENT · INSTANT DIGITAL ACCESS
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.55 }}
        className="relative"
      >
        <motion.div style={{ y }} className="relative w-full overflow-hidden">
          <a
            href={site.checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-banner-link"
            data-analytics-event="cta_click"
            data-analytics-cta="hero-banner"
            aria-label="Get instant access to Wild Woman Millions — $27"
            className="block cursor-pointer"
          >
            <img
              src={`/assets/${site.heroBanner}`}
              alt="Wild Woman Millions — Dasha Love with leopard, Love Bus, books. Normally $777, now $27. Get instant access."
              className="mx-auto h-auto w-full max-w-[1700px] object-contain transition-transform duration-700 hover:scale-[1.01]"
              fetchPriority="high"
              data-testid="hero-bundle-image"
            />
          </a>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-ink/60 to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};
